import { type CartUpdateAction } from "@commercetools/platform-sdk"
import {
    JC_FRIEND_SKU,
    addBillingAddressBase,
    addLoyaltyCardNumberBase,
    addProductsToCart,
    addShippingAddressBase,
    applyGiftcardBase,
    assignCart,
    cleanCostBase,
    createCart,
    getCart,
    getShippingMethodsBase,
    gtmCartRecovery,
    localStorage,
    recalculateCartBase,
    removeGiftcardBase,
    removePaymentCostBase,
    replicateCartBase,
    setPaymentCostBase,
    setShippingCostBase,
    typedBoolean,
    updateLineItem,
    validateCartBase,
} from "@jog/shared"
import {
    BillingAddressForm,
    CartMessageWithSnapshot,
    CartViewResponse,
    JoggCart,
    ShippingAddressForm,
    ShippingMethod,
} from "@jog/types"
import { autorun, makeAutoObservable } from "mobx"

function cartAddressToAddressForm(
    cartAddress: JoggCart["shippingAddress"] | JoggCart["billingAddress"]
): Omit<ShippingAddressForm, "psfKey"> | undefined {
    if (!cartAddress) return undefined
    const [insertion, ...lastName] = (cartAddress.lastName || "").split(" ")
    return {
        name: cartAddress.firstName,
        insertion: insertion || "",
        familyName: lastName.join(" "),
        email: cartAddress.email,
        phoneNumber: cartAddress.phone,
        country: cartAddress.country,
        postalCode: cartAddress.postalCode,
        city: cartAddress.city,
        streetName: cartAddress.streetName,
        houseNumber: cartAddress.streetNumber,
        addition: cartAddress.additionalStreetInfo || "",
    }
}
export interface CartStoreApi {
    recalculateCart: ReturnType<typeof recalculateCartBase>
    validateCart: ReturnType<typeof validateCartBase>
    addBillingAddress: ReturnType<typeof addBillingAddressBase>
    addShippingAddress: ReturnType<typeof addShippingAddressBase>
    getShippingMethods: ReturnType<typeof getShippingMethodsBase>
    setShippingCost: ReturnType<typeof setShippingCostBase>
    applyGiftcard: ReturnType<typeof applyGiftcardBase>
    removeGiftcard: ReturnType<typeof removeGiftcardBase>
    replicateCart: ReturnType<typeof replicateCartBase>
    removePaymentCost: ReturnType<typeof removePaymentCostBase>
    setPaymentCost: ReturnType<typeof setPaymentCostBase>
    addLoyaltyCardNumber?: ReturnType<typeof addLoyaltyCardNumberBase>
    cleanCost: ReturnType<typeof cleanCostBase>
}

export interface CartStoreOptions<T extends CartStoreApi> {
    api: T
}

export class CartStore<T extends CartStoreApi = CartStoreApi> {
    cartView: null | CartViewResponse = null
    isCartViewLoaded = false
    isCtaDisabled = false
    disableCheckoutButton = false
    isCheckoutBillingFormError = false
    isDeliverAddressError = false
    shippingMethods: ShippingMethod[] = []
    isGiftcardError = false

    syncCart(cart: JoggCart) {
        this.cartView = { body: cart } as CartViewResponse
        localStorage.setItem<CartViewResponse>("createdCart", this.cartView)
        this.isCartViewLoaded = true
    }
    private initCart() {
        const _cartView = localStorage.getItem<CartViewResponse>("createdCart")
        if (_cartView) {
            this.cartView = _cartView
            this.isCartViewLoaded = true
        }
    }

    constructor(public options: CartStoreOptions<T>) {
        makeAutoObservable(this, {}, { autoBind: true })
        autorun(async (reaction) => {
            this.initCart()
            reaction.dispose()
        })
    }

    private setCart(cart: CartViewResponse) {
        localStorage.setItem<CartViewResponse>("createdCart", cart)
        this.cartView = cart
    }

    get billingAddress(): BillingAddressForm | undefined {
        return cartAddressToAddressForm(this.cartView?.body?.billingAddress)
    }
    get shippingAddress(): ShippingAddressForm | undefined {
        return cartAddressToAddressForm(this.cartView?.body?.shippingAddress)
    }
    get cartLineItems() {
        return this.cartView?.body?.lineItems?.filter((lineItem) => lineItem.sku !== JC_FRIEND_SKU)
    }

    get isJcFriendInCart() {
        return this.cartView?.body?.lineItems?.find((lineItem) => lineItem.sku === JC_FRIEND_SKU)
    }

    get isOnlyJcFriendInCart() {
        return this.cartView?.body?.lineItems?.length === 1 && this.isJcFriendInCart
    }

    get cartTotalItems() {
        const totalQuantityOfItems = this.cartView?.body?.totalQuantityOfItems || 0
        return this.isJcFriendInCart ? totalQuantityOfItems - 1 : totalQuantityOfItems
    }

    get cartLineItemsCount() {
        const lineItems = this.cartView?.body?.lineItems
        if (!lineItems) return 0
        return this.isJcFriendInCart ? Number(lineItems.length) - 1 : lineItems.length
    }

    get getCartPrices() {
        const cartViewBody = this.cartView?.body
        const originSubTotal =
            cartViewBody?.lineItems
                ?.filter((item) => typedBoolean(item.prices?.totalPrice?.centAmount))
                ?.map((item) => item.prices?.firstPriceInTheCart?.centAmount * item.quantity)
                ?.reduce((prev, current) => prev + current, 0) || 0
        const totalPrice = cartViewBody?.total?.centAmount || 0
        const shippingCosts = cartViewBody?.shippingCosts?.costs?.centAmount || 0
        const paymentCosts = cartViewBody?.paymentCosts?.centAmount || 0
        const subTotal = totalPrice - shippingCosts - paymentCosts
        const subTotalWithoutPaymentCosts = totalPrice - paymentCosts
        const totalDiscount = originSubTotal - subTotal
        return {
            subTotal,
            originSubTotal,
            totalDiscount,
            totalPrice,
            shippingCosts,
            paymentCosts,
            subTotalWithoutPaymentCosts,
        }
    }

    /* Assign cart to user when logging in with a cart as anonymous user */
    async assignCartFromAnonymousUser() {
        try {
            const cartView = localStorage.getItem<CartViewResponse>("createdCart")
            const newCart = await assignCart(cartView?.body?.id || "")
            this.setCart(newCart)
            return true
        } catch (e) {
            console.log(e)
        }
    }

    async getCartView(cartId: string) {
        try {
            let response = await getCart(cartId)
            if (response.body.cartState !== "Active") {
                const replicateResponse = await this.options.api.replicateCart(cartId)
                if (replicateResponse && replicateResponse.statusCode === 200) {
                    response = await getCart(replicateResponse.body.replicatedCartId)
                }
            }
            if (response?.statusCode >= 200 && response?.statusCode < 300) {
                this.setCart(response)
                this.isCartViewLoaded = true
            }
            return this.isCartViewLoaded
        } catch (e) {
            console.log(e)
        }
    }

    actionList: JoggCart["cartMessage"] = []

    removeActionList(id: string) {
        const actions = [...this.actionList]
        const index = this.actionList
            .filter((i) => i.action.action !== "removeDiscountCode")
            .findIndex((i) => (i as CartMessageWithSnapshot).snapshot.id === id)
        if (index > -1) actions.splice(index, 1)
        this.actionList = [...actions]
    }

    clearActionList() {
        this.actionList = []
    }

    async validateCart() {
        this.disableCheckoutButton = true
        this.actionList = []
        try {
            const actionList = await this.options.api
                .validateCart(this.cartView!.body.id)
                .then((res) => res.body?.actionList || [])
            await this.getCartView(this.cartView!.body.id)
            this.actionList = actionList
        } catch (e) {
            console.log(e)
        } finally {
            this.disableCheckoutButton = false
        }
    }

    /* Recalculate cart and get card view to refresh */
    async recalculateCart() {
        if (!this.cartView) return
        this.disableCheckoutButton = true
        try {
            const response = await this.options.api.recalculateCart(this.cartView!.body.id, this.cartView!.body.version)
            if (!response) await this.recoverBrokenCart()
            await this.getCartView(this.cartView!.body.id)
            this.isCartViewLoaded = true
        } catch (e) {
            console.log(e)
        } finally {
            this.disableCheckoutButton = false
        }
    }

    async addProductsToCart(products: { sku: string; quantity: number }[]) {
        try {
            /* Disable CTA while executing method */
            this.isCtaDisabled = true
            this.initCart() // Make sure the "createdCart" is loaded and set it as this.cartView
            if (!this.cartView) {
                const cart = await createCart()
                this.syncCart(cart)
                await this.addProductsToCart(products)
                return
            }
            const cart = await addProductsToCart(this.cartView.body.id, products)
            gtmCartRecovery(cart)
            if (!cart) {
                await this.recoverBrokenCart()
            } else {
                this.syncCart(cart)
            }
        } catch (e) {
            console.log(e)
        } finally {
            this.isCtaDisabled = false
        }
    }

    async updateLineItem(lineItemId: string, quantity: number) {
        this.isCtaDisabled = true
        try {
            const cart = await updateLineItem(this.cartView!.body.id, lineItemId, quantity)
            gtmCartRecovery(cart)
            if (!cart) {
                await this.recoverBrokenCart()
            } else {
                this.syncCart(cart)
            }
        } catch (e) {
            console.log(e)
        } finally {
            this.isCtaDisabled = false
        }
    }

    /* Add billing information to cart */
    async addBillingInformation(billingAddress: BillingAddressForm) {
        this.isCheckoutBillingFormError = false
        this.isCtaDisabled = true
        try {
            const response = await this.options.api.addBillingAddress(
                this.cartView!.body.id,
                this.cartView!.body.version,
                billingAddress
            )

            if (response && response.statusCode === 200) {
                await this.getCartView(this.cartView!.body.id)
            } else {
                this.isCheckoutBillingFormError = true
                window.scrollTo({ top: 0, behavior: "smooth" })
            }
        } catch (e) {
            console.log(e)
            this.isCheckoutBillingFormError = true
            window.scrollTo({ top: 0, behavior: "smooth" })
        } finally {
            this.isCtaDisabled = false
        }
    }

    /* Add shipping information to cart */
    async addShippingInformation(shippingAddress: ShippingAddressForm) {
        this.isDeliverAddressError = false
        this.isCtaDisabled = true
        if (shippingAddress.email === undefined) {
            shippingAddress.email = this.billingAddress?.email
        }
        try {
            const response = await this.options.api.addShippingAddress(
                this.cartView!.body.id,
                this.cartView!.body.version,
                shippingAddress
            )
            if (response && response.statusCode === 200) {
                await this.getCartView(this.cartView!.body.id)
            } else {
                this.isDeliverAddressError = true
            }
        } catch (e) {
            console.log(e)
        } finally {
            this.isCtaDisabled = false
        }
    }

    async getShippingMethods() {
        if (!this.cartView?.body) return
        if (!this.shippingAddress) return
        try {
            const { id } = this.cartView.body
            const response = await this.options.api.getShippingMethods(id)
            if (response.statusCode === 200) {
                // response.body.results[0] => id is used to set shippingCost
                // response.body.results[1] => id is used to set shippingCost to 0
                this.shippingMethods = response.body.results.filter(
                    (e) => !e.key?.startsWith("standard_shipping_method")
                )
            }
        } catch (e) {
            console.log(e)
        }
    }

    async setShippingCost(shippingMethodId: string) {
        if (!this.cartView?.body) return
        if (!shippingMethodId) return
        this.isCtaDisabled = true
        try {
            const { id, version } = this.cartView.body
            await this.options.api.setShippingCost(id, shippingMethodId, version)
            await this.getCartView(id)
        } catch (e) {
            console.log(e)
        } finally {
            this.isCtaDisabled = false
        }
    }

    async applyGiftcard(giftcardCode: string) {
        if (!this.cartView?.body) return
        this.isCtaDisabled = true
        try {
            const { id } = this.cartView.body
            const response = await this.options.api.applyGiftcard(id, giftcardCode)
            if (response && response.statusCode === 200) {
                this.isGiftcardError = false
                await this.getCartView(id)
                this.isCtaDisabled = false
                return true
            } else {
                this.isGiftcardError = true
                console.log("Giftcard error")
            }
        } catch (e) {
            this.isGiftcardError = true
            console.log(e)
        } finally {
            this.isCtaDisabled = false
        }
        return false
    }

    async removeGiftcard(giftcardCode: string, isRemoveAllGiftcards?: boolean) {
        if (!this.cartView?.body) return
        if (!isRemoveAllGiftcards) this.isCtaDisabled = true
        try {
            const { id } = this.cartView.body
            const response = await this.options.api.removeGiftcard(id, giftcardCode)
            if (response && response.statusCode === 200) {
                await this.getCartView(id)
            } else {
                console.log("Giftcard deletion error")
                if (!isRemoveAllGiftcards) this.isCtaDisabled = false
            }
        } catch (e) {
            console.log(e)
        }
        if (!isRemoveAllGiftcards) this.isCtaDisabled = false
    }

    async removeAllGiftcards() {
        const giftcardsObj = this.cartView!.body.appliedGiftcards
        if (!giftcardsObj) return
        this.isCtaDisabled = true
        const validGiftcards = giftcardsObj.giftcards.filter((e) => e !== undefined)
        for (let i = validGiftcards.length - 1; i >= 0; i--) {
            const code = validGiftcards[i].code
            if (code) {
                await this.removeGiftcard(code, true)
            }
        }
        this.isCtaDisabled = false
    }

    async recoverBrokenCart() {
        const replicateResponse = await this.options.api.replicateCart(this.cartView!.body.id)
        if (replicateResponse && replicateResponse.statusCode === 200) {
            await this.getCartView(replicateResponse.body.replicatedCartId)
            window.location.reload()
        }
    }

    async removePaymentCost() {
        if (this.cartView?.body?.paymentCosts?.centAmount) {
            this.isCtaDisabled = true
            try {
                const { id, version, paymentCosts } = this.cartView.body
                await this.options.api.removePaymentCost(id, version, paymentCosts?.id)
                await this.getCartView(this.cartView.body.id)
            } catch (e) {
                console.log(e)
            }
            this.isCtaDisabled = false
        }
    }

    async addLoyaltyCardNumber(loyaltyCardNumber: string) {
        if (!this.options.api.addLoyaltyCardNumber) return
        if (!this.cartView?.body) return
        this.isCtaDisabled = true
        try {
            const { id, version } = this.cartView.body
            await this.options.api.addLoyaltyCardNumber(id, version, loyaltyCardNumber)
            await this.getCartView(id)
        } catch (e) {
            console.log(e)
        }
        this.isCtaDisabled = false
    }

    async setPaymentCost(paymentMethodExtraCost: number, paymentName: string) {
        if (!this.cartView?.body) return
        this.isCtaDisabled = true
        const { id, version } = this.cartView.body
        try {
            if (this.getCartPrices.paymentCosts) {
                await this.removePaymentCost()
            }
            await this.options.api.setPaymentCost(id, version, paymentName, paymentMethodExtraCost)
            await this.getCartView(id)
        } catch (e) {
            console.log(e)
        }
        this.isCtaDisabled = false
    }

    async cleanCosts() {
        if (!this.cartView?.body) return
        const { id, version, paymentCosts, shippingAddress } = this.cartView.body
        this.isCtaDisabled = true
        try {
            const actions: CartUpdateAction[] = shippingAddress
                ? []
                : [
                      {
                          action: "setShippingAddress",
                          address: {
                              country:
                                  process.env.NEXT_PUBLIC_COUNTRY === "US"
                                      ? "AT"
                                      : process.env.NEXT_PUBLIC_COUNTRY || "AT",
                          },
                      },
                      {
                          action: "setShippingMethod",
                          shippingMethod: {
                              key: "home_delivery",
                              typeId: "shipping-method",
                          },
                      },
                  ]
            await this.options.api.cleanCost(id, version, actions, paymentCosts?.id)
            await this.getCartView(id)
        } catch (e) {
            console.log(e)
        }
        this.isCtaDisabled = false
    }

    getFreeShippingRemaining(thresholdAmount: number) {
        if (!this.cartView?.body) return 0
        const thresholdCents = ((thresholdAmount || 75) as number) * 100
        const remainingCents = Math.max(0, thresholdCents - (this.getCartPrices.subTotal || 0))
        return remainingCents
    }
}
